import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { computeStratigraphyCosts } from '@/components/configurator-v2/hooks/computeStratigraphyCosts';
import { useCurrentOrganization } from '@/contexts/OrganizationContext';
import type { LayerV2 } from '@/components/configurator-v2/types';

/**
 * Prezzo €/m² calcolato DAL VIVO per un elenco di stratigrafie, con i netti
 * dell'organizzazione corrente.
 *
 * Perché serve. Le card del catalogo mostravano `stratigraphies.comprehensive_cost_per_sqm`,
 * un valore scritto una volta al salvataggio della parete e mai più aggiornato:
 * 261 pareti su 317 erano più vecchie dell'ultima modifica ai materiali (tempi
 * guida cambiati due volte, prezzi RProduzioni, isolanti). Su PV006 la card
 * diceva 41,94 €/m² quando il costo reale era ~43,37.
 *
 * Perché NON si risolve ricalcolando e riscrivendo quel campo:
 *  - 250 delle 317 pareti sono del catalogo GLOBALE e la RLS ne vieta l'UPDATE
 *    a chi non è admin di piattaforma → un "aggiorna tutto" salterebbe in
 *    silenzio il 79% del catalogo;
 *  - soprattutto, su una parete globale non esiste UN costo giusto: dipende
 *    dagli sconti di chi guarda. Scrivercelo dentro ripeterebbe la stessa
 *    contaminazione che avevamo su `materials.unit_price`.
 *
 * Quindi non si scrive niente: si calcola al volo, per l'org corrente.
 *
 * Costo: 3 query in tutto (layer+materiali, netti, costo orario) a prescindere
 * da quante pareti ci sono — non una per card.
 *
 * NB — SOLO PARETI. I controsoffitti restano sul valore salvato: il pendino
 * scelto non è memorizzato sulla stratigrafia, quindi un calcolo dal vivo li
 * sottostimerebbe (mancherebbe la riga pendini). Vedi `computeCeilingCosts`.
 */
export function useStratigraphiesLivePrices(stratigraphyIds: string[]) {
  const { currentOrganizationId } = useCurrentOrganization();
  // Chiave stabile: l'ordine dell'elenco non deve invalidare la cache.
  const idsKey = [...stratigraphyIds].sort().join(',');

  return useQuery({
    // L'org fa parte della chiave: cambiando azienda cambiano i netti.
    queryKey: ['stratigraphies-live-prices', currentOrganizationId, idsKey],
    enabled: stratigraphyIds.length > 0,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<Map<string, number>> => {
      const result = new Map<string, number>();
      if (stratigraphyIds.length === 0) return result;

      // Si chiedono SOLO i campi che `computeStratigraphyCosts` legge davvero:
      // la lista puo' avere 300+ pareti e ~2500 layer, non serve trascinarsi
      // dietro l'intera anagrafica materiale per ognuno. (La larghezza del
      // montante non serve piu': l'isolante ora vale 1 m²/m².)
      const { data: strats, error } = await supabase
        .from('stratigraphies')
        .select(`
          id,
          type,
          stud_spacing_mm,
          layers (
            id,
            position,
            material_id,
            screw_material_id,
            screw_quantity,
            screw_cost_per_sqm,
            anchor_material_id,
            material_quantity_per_sqm,
            materials!layers_material_id_fkey (
              id, name, category, unit, unit_price, list_price,
              incidence_per_sqm, box_pieces,
              installation_time_per_sqm
            ),
            screw_materials:materials!layers_screw_material_id_fkey (
              id, name, unit, unit_price, box_pieces, installation_time_per_sqm
            ),
            anchor_materials:materials!layers_anchor_material_id_fkey (
              id, name, unit, unit_price, box_pieces, installation_time_per_sqm
            )
          )
        `)
        .in('id', stratigraphyIds);

      if (error || !strats) {
        console.warn('[useStratigraphiesLivePrices] fetch fallito:', error?.message);
        return result;
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const rows = strats as any[];

      // Un solo giro di prezzi netti per TUTTI i materiali coinvolti.
      const matIds = new Set<string>();
      for (const s of rows) {
        for (const l of (s.layers ?? [])) {
          if (l?.materials?.id) matIds.add(l.materials.id);
          if (l?.screw_materials?.id) matIds.add(l.screw_materials.id);
          if (l?.anchor_materials?.id) matIds.add(l.anchor_materials.id);
        }
      }
      const priceMap = new Map<string, { unit_price: number; installation_time_per_sqm: number | null }>();
      if (matIds.size > 0) {
        const { data: pricing, error: pricingError } = await supabase
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .from('materials_with_pricing' as any)
          .select('id, unit_price, installation_time_per_sqm')
          .in('id', Array.from(matIds));
        if (pricingError) {
          // Senza i netti mostreremmo prezzi di listino: meglio lasciare la
          // card sul valore salvato che darne uno gonfiato.
          console.warn('[useStratigraphiesLivePrices] netti non caricati:', pricingError.message);
          return result;
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        for (const p of (pricing ?? []) as any[]) {
          priceMap.set(p.id, {
            unit_price: Number(p.unit_price ?? 0),
            installation_time_per_sqm:
              p.installation_time_per_sqm != null ? Number(p.installation_time_per_sqm) : null,
          });
        }
      }

      const { data: settingData } = await supabase
        .from('configurator_settings')
        .select('value')
        .eq('key', 'cost_per_hour')
        .maybeSingle();
      const costPerHour = parseFloat(settingData?.value ?? '30') || 30;

      for (const s of rows) {
        if (String(s.type ?? '') === 'ceiling') continue; // vedi nota in testa
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const dbLayers: any[] = s.layers ?? [];
        if (dbLayers.length === 0) continue;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const sorted = [...dbLayers].sort((a: any, b: any) => a.position - b.position);
        const layersForCompute = sorted.map((l, idx) => {
          const mat = l.materials ? { ...l.materials } : null;
          const screw = l.screw_materials ? { ...l.screw_materials } : null;
          const anchor = l.anchor_materials ? { ...l.anchor_materials } : null;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const applica = (m: any) => {
            const eff = m?.id ? priceMap.get(m.id) : undefined;
            if (!eff) return;
            m.unit_price = eff.unit_price;
            m.installation_time_per_sqm = eff.installation_time_per_sqm;
          };
          applica(mat);
          applica(screw);
          applica(anchor);
          return {
            id: l.id ?? `tmp-${idx}`,
            position: idx + 1,
            materialId: l.material_id ?? null,
            material: mat,
            screwMaterialId: l.screw_material_id ?? null,
            screwMaterial: screw,
            screwQuantity: l.screw_quantity ?? undefined,
            screwCostPerSqm: l.screw_cost_per_sqm ?? undefined,
            anchorMaterialId: l.anchor_material_id ?? null,
            anchorMaterial: anchor,
            materialQuantity: l.material_quantity_per_sqm ?? null,
          };
        }) as unknown as LayerV2[];

        const breakdown = computeStratigraphyCosts(
          layersForCompute,
          Number(s.stud_spacing_mm ?? 600),
          costPerHour,
        );
        if (breakdown.totalCost > 0) {
          result.set(s.id, Math.round(breakdown.totalCost * 100) / 100);
        }
      }

      return result;
    },
  });
}
